// ============================================================================
// FSA POLICY NOTE — TipTap rich-text editor (controlled allow-list)
// ============================================================================
//
// Thin React wrapper around @tiptap/react with the explicit extension
// allow-list that POLICY notes use:
//   · paragraphs + text
//   · bold / italic / underline marks
//   · bullet + ordered lists
//   · tables (insert / delete row / column, header row toggle — no
//     merge / split / cell styling)
//
// Returns the document as TipTap JSON via onChange. Save layer
// stores the doc verbatim into workspace.policy_section_data; render
// layer applies its own allow-list at print time.

import React, { useEffect } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import IconSystem from 'icon-collection/icon-systems';

// ---------------------------------------------------------------------------
// Inject editor styles once per module load. TipTap ships zero CSS for
// tables / lists / paragraphs, so the editor renders cells as plain text
// with no borders — auditors can't see the table structure while editing.
// Scoped to `.ProseMirror.tiptap-fsa-editor` so it never bleeds into
// other content. Mirrors the conventional ProseMirror table styling
// the TipTap team publishes.
// ---------------------------------------------------------------------------
const EDITOR_STYLE_ID = 'fsa-tiptap-editor-styles';
if (typeof document !== 'undefined' && !document.getElementById(EDITOR_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = EDITOR_STYLE_ID;
  style.textContent = `
.ProseMirror.tiptap-fsa-editor {
  outline: none;
}
.ProseMirror.tiptap-fsa-editor > * + * {
  margin-top: 0.5em;
}
.ProseMirror.tiptap-fsa-editor p { margin: 0; }
.ProseMirror.tiptap-fsa-editor ul,
.ProseMirror.tiptap-fsa-editor ol {
  padding-left: 1.25rem;
  margin: 0.25rem 0;
}
.ProseMirror.tiptap-fsa-editor li > p { margin: 0; }

/* Tables — visible borders, header row distinction, selected-cell hint */
.ProseMirror.tiptap-fsa-editor table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0.5rem 0;
  overflow: hidden;
  border: 1px solid #94a3b8;
}
.ProseMirror.tiptap-fsa-editor table td,
.ProseMirror.tiptap-fsa-editor table th {
  min-width: 2em;
  border: 1px solid #cbd5e1;
  padding: 4px 6px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}
.ProseMirror.tiptap-fsa-editor table th {
  font-weight: 600;
  text-align: left;
  background-color: #eef3f9;
  border-bottom: 1px solid #94a3b8;
}
/* TipTap marks the active cell with .selectedCell — give it a faint tint
   so the auditor always knows which cell the cursor is in. */
.ProseMirror.tiptap-fsa-editor table .selectedCell {
  background-color: rgba(37, 99, 235, 0.08);
}
.ProseMirror.tiptap-fsa-editor table .selectedCell::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.45);
}
/* When a column / row is being resized — TipTap adds .column-resize-handle */
.ProseMirror.tiptap-fsa-editor table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #2563eb;
  pointer-events: none;
}
.ProseMirror.tiptap-fsa-editor table p { margin: 0; }
`;
  document.head.appendChild(style);
}

interface Props {
  /** Initial document (TipTap JSON). Undefined → empty paragraph. */
  initialContent?: unknown;
  /**
   * Called on every change with the document JSON. Use this to push
   * into workspace.policy_section_data[<code>].content.
   */
  onChange: (doc: unknown) => void;
  /** Disabled (read-only) when true. */
  readOnly?: boolean;
  /**
   * Key changes force a remount — pass section code so switching
   * sections wipes editor state cleanly.
   */
  sectionKey?: string;
}

const TiptapEditor: React.FC<Props> = ({ initialContent, onChange, readOnly = false, sectionKey }) => {
  const editor = useEditor({
    extensions: [
      // StarterKit ships paragraph, bold, italic, lists, history,
      // hard-break, etc. We disable codeBlock / blockquote / heading /
      // horizontalRule / strike — keep the policy-note surface tight.
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        heading: false,
        horizontalRule: false,
        strike: false,
        code: false,
      }),
      Underline,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: initialContent ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    editable: !readOnly,
    editorProps: {
      // Tag the ProseMirror root so the scoped table / list styles
      // injected above attach without bleeding into other rich-text
      // surfaces that may be added later.
      attributes: { class: 'tiptap-fsa-editor' },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getJSON());
    },
    // Section-keyed identity — remount on section switch
    // (see useEditor immutableProps behaviour).
  }, [sectionKey]);

  // Sync editable when readOnly prop toggles mid-life.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  if (!editor) {
    return (
      <div style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Loading editor…
      </div>
    );
  }

  return (
    <div style={editorWrapStyle}>
      {!readOnly && <Toolbar editor={editor} />}
      <EditorContent editor={editor} style={editorContentStyle} />
    </div>
  );
};

// ============================================================================
// TOOLBAR
// ============================================================================

const Toolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const inTable = editor.isActive('table');

  // Wrap each command so it focuses the editor first. Useful because the
  // toolbar button receives focus on click — without `.focus()` first,
  // TipTap commands fire against a stale selection (or skip silently
  // when the cursor wasn't in a table when the button was clicked).
  const cmd = (fn: () => void) => () => { editor.commands.focus(); fn(); };

  return (
    <div style={toolbarColumnStyle}>
      {/* Main toolbar — text formatting, lists, insert-table */}
      <div style={toolbarStyle}>
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <strong style={{ fontSize: '0.85rem' }}>B</strong>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <em style={{ fontSize: '0.85rem', fontWeight: 500 }}>I</em>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)"
        >
          <span style={{ fontSize: '0.85rem', textDecoration: 'underline' }}>U</span>
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <IconSystem.list size={14} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          <span style={{ fontWeight: 600, fontSize: '0.7rem' }}>1.</span>
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert a new 3-row × 3-column table"
        >
          <IconSystem.grid size={14} />
          <span style={{ marginLeft: 4, fontSize: '0.72rem' }}>Insert table</span>
        </ToolbarButton>
      </div>

      {/* Table sub-toolbar — ALWAYS visible. Buttons disable when the cursor
          is outside any table so the auditor can see the controls exist
          regardless of whether the cursor is currently parked. */}
      <div style={{ ...tableToolbarStyle, opacity: inTable ? 1 : 0.55 }}>
        <span style={tableGroupLabelStyle}>Row:</span>
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addRowBefore().run())}
          disabled={!inTable}
          title={inTable ? 'Add row above the current row' : 'Click inside a table cell first'}
          label="+ Above"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addRowAfter().run())}
          disabled={!inTable}
          title={inTable ? 'Add row below the current row' : 'Click inside a table cell first'}
          label="+ Below"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteRow().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the current row' : 'Click inside a table cell first'}
          label="− Delete"
          danger
        />

        <Divider />

        <span style={tableGroupLabelStyle}>Column:</span>
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!inTable}
          title={inTable ? 'Add column to the left' : 'Click inside a table cell first'}
          label="+ Left"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().addColumnAfter().run())}
          disabled={!inTable}
          title={inTable ? 'Add column to the right' : 'Click inside a table cell first'}
          label="+ Right"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteColumn().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the current column' : 'Click inside a table cell first'}
          label="− Delete"
          danger
        />

        <Divider />

        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().toggleHeaderRow().run())}
          disabled={!inTable}
          title={inTable ? 'Toggle whether the top row is the table header' : 'Click inside a table cell first'}
          label="Header row"
        />
        <LabeledToolbarButton
          onClick={cmd(() => editor.chain().focus().deleteTable().run())}
          disabled={!inTable}
          title={inTable ? 'Delete the entire table' : 'Click inside a table cell first'}
          label="Delete table"
          danger
        />

        {!inTable && (
          <span style={tableHintStyle}>
            Click inside a table cell to enable
          </span>
        )}
      </div>
    </div>
  );
};

const LabeledToolbarButton: React.FC<{
  onClick: () => void;
  title: string;
  label: string;
  danger?: boolean;
  disabled?: boolean;
}> = ({ onClick, title, label, danger, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      ...labeledBtnStyle,
      color: disabled ? 'var(--text-muted, #94a3b8)' : (danger ? '#dc2626' : 'var(--text-primary)'),
      borderColor: disabled ? 'var(--border-color)' : (danger ? 'rgba(220, 38, 38, 0.35)' : 'var(--border-color)'),
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? 'transparent' : 'var(--bg-primary)',
    }}
  >
    {label}
  </button>
);

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ active, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      ...toolbarBtnStyle,
      background: active ? 'var(--bg-secondary)' : 'transparent',
      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
    }}
  >
    {children}
  </button>
);

const Divider: React.FC = () => (
  <div style={{
    width: 1, height: 18,
    background: 'var(--border-color)',
    margin: '0 4px',
  }} />
);

// ============================================================================
// STYLES
// ============================================================================

const editorWrapStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  overflow: 'hidden',
  background: 'var(--bg-primary)',
};
const toolbarColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};
const toolbarStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
  gap: 2,
  padding: '4px 6px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const tableToolbarStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
  gap: 4,
  padding: '4px 6px',
  borderBottom: '1px solid var(--border-color)',
  background: '#eef3f9',
};
const tableGroupLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#1e3a5f',
  marginLeft: 4,
  marginRight: 2,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
const tableHintStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '2px 8px',
  fontSize: '0.7rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
  letterSpacing: 0.2,
};
const toolbarBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 26, height: 26,
  padding: '0 6px',
  border: 'none', borderRadius: 3,
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const labeledBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  height: 24,
  padding: '0 10px',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary)',
  cursor: 'pointer',
  fontSize: '0.72rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const editorContentStyle: React.CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: 'var(--text-primary)',
  minHeight: 220,
  overflow: 'auto',
};

export default TiptapEditor;
